/**
 * DeveloperAgentsPage
 * Main dashboard for Developer Agent feature - manage code analysis, generated agents and skills
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '../../components/layout';
import { ConnectRepoForm, RepoCard, AnalysisProgress } from '../../components/developer-agents';
import { developerAgentApi } from '../../services/api';
import type { ConnectedRepo, DevAgentStats } from '../../types';
import styles from './DeveloperAgentsPage.module.css';

export const DeveloperAgentsPage: React.FC = () => {
  // State
  const [stats, setStats] = useState<DevAgentStats | null>(null);
  const [repos, setRepos] = useState<ConnectedRepo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeAnalysis, setActiveAnalysis] = useState<{
    repoName: string;
    analysisId: string;
  } | null>(null);

  // Load stats and repos
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statsResponse, reposResponse] = await Promise.all([
        developerAgentApi.getStats(),
        developerAgentApi.getConnectedRepos(),
      ]);

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }

      if (reposResponse.success && reposResponse.data) {
        setRepos(reposResponse.data);
      }
    } catch (err) {
      console.error('Failed to load developer agent data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle analysis start
  const handleAnalyzeStart = (repoName: string, analysisId: string) => {
    setActiveAnalysis({ repoName, analysisId });
  };

  // Handle analysis complete
  const handleAnalysisComplete = () => {
    setActiveAnalysis(null);
    loadData(); // Reload data to show updated repo
  };

  // Handle re-analyze
  const handleReAnalyze = async (repoId: string) => {
    try {
      const repo = repos.find((r) => r.id === repoId);
      if (!repo) return;

      const response = await developerAgentApi.syncRepo(repoId);
      if (response.success && response.data) {
        setActiveAnalysis({
          repoName: repo.fullName,
          analysisId: response.data.analysisId,
        });
      }
    } catch (err) {
      console.error('Failed to re-analyze repository:', err);
      setError('Failed to start re-analysis');
    }
  };

  // Handle push to repo
  const handlePushToRepo = (repoId: string) => {
    // TODO: Open PushToRepo modal
    console.log('Push to repo:', repoId);
  };

  return (
    <AppLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Developer Agents</h1>
            <p className={styles.subtitle}>
              Connect GitHub repositories to auto-generate custom developer agents and skills
            </p>
          </div>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className={styles.statsBar}>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{stats.connectedRepos}</div>
              <div className={styles.statLabel}>Connected Repos</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{stats.generatedAgents}</div>
              <div className={styles.statLabel}>Generated Agents</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{stats.generatedSkills}</div>
              <div className={styles.statLabel}>Generated Skills</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{stats.prsCreated}</div>
              <div className={styles.statLabel}>PRs Created</div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loadingState}>Loading...</div>
          ) : error ? (
            <div className={styles.errorState}>
              <p>{error}</p>
              <button onClick={loadData}>Retry</button>
            </div>
          ) : (
            <>
              {/* Active Analysis Progress */}
              {activeAnalysis && (
                <AnalysisProgress
                  analysisId={activeAnalysis.analysisId}
                  repoName={activeAnalysis.repoName}
                  onComplete={handleAnalysisComplete}
                  onError={(err) => {
                    setError(err);
                    setActiveAnalysis(null);
                  }}
                />
              )}

              {/* Connect New Repo Section */}
              <div className={styles.connectSection}>
                <h2>Connect Repository</h2>
                <p>Connect a new GitHub repository to analyze and generate developer agents</p>
                <ConnectRepoForm
                  onAnalyzeStart={handleAnalyzeStart}
                  onError={setError}
                />
              </div>

              {/* Connected Repos Grid */}
              <div className={styles.reposSection}>
                <h2>Connected Repositories</h2>
                {repos.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>No repositories connected yet</p>
                    <p>Connect your first repository to get started</p>
                  </div>
                ) : (
                  <div className={styles.reposGrid}>
                    {repos.map((repo) => (
                      <RepoCard
                        key={repo.id}
                        repo={repo}
                        onReAnalyze={handleReAnalyze}
                        onPushToRepo={handlePushToRepo}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default DeveloperAgentsPage;
